import {
  Children,
  cloneElement,
  useId,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { tokens } from "@core/theme/tokens";
import { portalPrimitiveValues as values } from "./portalPrimitiveValues";

/**
 * Tooltip propio con la estética del sitio (revisión en vivo 2026-09-22,
 * punto #10A). No existía ninguno en `src/`: el único precedente era el
 * atributo `title` nativo del navegador (BenefitsScreen.tsx), que no se puede
 * estilar, no aparece con el foco de teclado y tarda ~1s en mostrarse.
 *
 * Accesibilidad:
 *  · se revela con puntero (`mouseenter`) Y con foco de teclado (`focus`), que
 *    es lo que `title` no hace de forma confiable;
 *  · el texto vive siempre en el DOM, con `aria-describedby` permanente sobre
 *    el disparador: la descripción accesible se computa del elemento
 *    referenciado aunque esté oculto, así que el botón nunca queda sin nombre
 *    para un lector de pantalla. Ojo al escribir pruebas: mientras está
 *    oculto, el tooltip NO aparece en el árbol de roles que consultan
 *    `getByRole`/`axe` — hay que pedirlo con `{ hidden: true }`;
 *  · `Escape` lo cierra sin mover el foco, para quien navega con teclado y
 *    quiere sacarlo del medio.
 *
 * El hijo debe ser un único elemento que acepte `aria-describedby` y eventos
 * de puntero/foco: en la práctica el `<button>` de la acción.
 */
export function Tooltip({
  label,
  children,
  placement = "top",
}: {
  label: ReactNode;
  children: ReactElement;
  placement?: "top" | "bottom";
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const child = Children.only(children) as ReactElement<{
    "aria-describedby"?: string;
  }>;
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") setOpen(false);
      }}
    >
      {cloneElement(child, { "aria-describedby": id })}
      <span
        id={id}
        role="tooltip"
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          ...(placement === "top"
            ? { bottom: `calc(100% + ${values.tooltip.gap}px)` }
            : { top: `calc(100% + ${values.tooltip.gap}px)` }),
          zIndex: values.tooltip.zIndex,
          maxWidth: values.tooltip.maxWidth,
          width: "max-content",
          padding: values.tooltip.padding,
          borderRadius: values.tooltip.radius,
          border: `1px solid ${tokens.surface.line2}`,
          background: tokens.surface.bg3,
          color: tokens.text.primary,
          font: values.tooltip.font,
          boxShadow: tokens.shadow,
          textAlign: "center",
          pointerEvents: "none",
          opacity: open ? 1 : 0,
          visibility: open ? "visible" : "hidden",
          transition: "opacity 0.14s ease",
        }}
      >
        {label}
      </span>
    </span>
  );
}
